import { observer } from 'mobx-react-lite'
import { Link, useLocation } from 'react-router-dom'
import { Box } from '@mantine/core'
import { AppText } from '../common/ui'
import { useStore } from '../stores/store'
import {
  PRIMARY_NAV_ITEMS,
  ICON_BOX,
  ICON_GLYPH,
  isPrimaryNavActive
} from './primaryNavConfig'
import { TOP_NAV_HEIGHT } from './TopNavBar'

const NAV_BG = 'var(--gs-bg-1)'

interface PrimaryNavRailProps {
  variant: 'bottom' | 'sidebar';
  /** When false, only icons (mobile very narrow). */
  showLabels: boolean;
}

export default observer(function PrimaryNavRail ({ variant, showLabels }: PrimaryNavRailProps) {
  const location = useLocation()
  const { navigationStore, userStore } = useStore() // setActivePrimaryPath on click for MobX subscribers
  const username = userStore.user?.username
  const pathname = location.pathname

  const isBottom = variant === 'bottom'

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        ...(isBottom
          ? {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 100,
              minHeight: showLabels ? 72 : 56,
              background: NAV_BG,
              borderTop: '1px solid var(--gs-border)',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              overscrollBehaviorX: 'contain',
              '&::-webkit-scrollbar': { display: 'none' }
            }
          : {
              position: 'fixed',
              top: TOP_NAV_HEIGHT,
              left: 0,
              bottom: 0,
              zIndex: 99,
              width: 80,
              background: NAV_BG,
              borderRight: '1px solid var(--gs-border)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              paddingTop: 12,
              gap: 4
            })
      }}
    >
      {PRIMARY_NAV_ITEMS.map(({ to, label, IconActive, IconInactive }) => {
        const active = isPrimaryNavActive(to, pathname, username)
        const Icon = (active ? IconActive : IconInactive) as React.FC<{
          size?: number;
          color?: string;
        }>
        const color = active ? 'var(--gs-accent)' : 'var(--gs-muted)'

        return (
          <Box
            key={to}
            component={Link}
            to={to}
            onClick={() => navigationStore.setActivePrimaryPath(to)}
            sx={{
              flex: isBottom ? '0 0 auto' : undefined,
              flexShrink: 0,
              minWidth: isBottom ? (showLabels ? 68 : 52) : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: showLabels ? 4 : 0,
              textDecoration: 'none',
              padding: isBottom ? '6px 10px 8px' : '10px 4px',
              minHeight: isBottom ? undefined : 56,
              color,
              transition: 'color 0.15s ease, background 0.15s ease',
              ...(isBottom && active
                ? { boxShadow: 'inset 0 3px 0 0 var(--gs-accent)' }
                : {})
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: ICON_BOX,
                height: ICON_BOX,
                borderRadius: '50%',
                backgroundColor: active ? 'rgba(var(--gs-accent-rgb), 0.12)' : 'transparent',
                transition: 'background 0.15s ease'
              }}
            >
              <Icon size={ICON_GLYPH} color="currentColor" />
            </Box>
            {showLabels && (
              <AppText
                component="span"
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 10,
                  fontWeight: active ? 600 : 400,
                  letterSpacing: 0.2,
                  lineHeight: 1.1,
                  textAlign: 'center',
                  color: 'currentColor'
                }}
              >
                {label}
              </AppText>
            )}
          </Box>
        )
      })}
    </Box>
  )
})
